/**
 * 图层编辑节点卡片内容（镜像 DirectorContent）
 * 空态：图标 + 描述 + 「打开图层编辑」主按钮；
 * 已保存：缩略图全出血铺满（卡片已按画布比例变形），透明区显示棋盘格，右下角「图层编辑」悬浮入口
 */
import React, { memo } from 'react';
import { SquarePen } from 'lucide-react';
import { AppNode } from '@/types';

interface LayerEditContentProps {
  data: AppNode;
  onOpenLayerEdit?: (nodeId: string) => void;
}

const LayerEditContentComponent: React.FC<LayerEditContentProps> = memo(({
  data,
  onOpenLayerEdit,
}) => {
  const handleOpen = (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    onOpenLayerEdit?.(data.id);
  };

  const thumbnail = (data.data as any)?.thumbnailDataUrl as string | undefined;

  // 进入过编辑器并保存：画布静态缩略图全出血铺满（无实时渲染开销），双击或右下角入口打开编辑器
  if (thumbnail) {
    return (
      <div className="relative w-full h-full overflow-hidden" onDoubleClick={handleOpen}>
        {/* 透明区棋盘格（与编辑器画布透明底语义一致） */}
        <div
          className="absolute inset-0"
          style={{
            backgroundImage: 'conic-gradient(#333333 25%, #1f1f1f 0 50%, #333333 0 75%, #1f1f1f 0)',
            backgroundSize: '20px 20px',
          }}
        />
        <img
          src={thumbnail}
          alt="图层编辑缩略图"
          className="absolute inset-0 w-full h-full object-cover"
          draggable={false}
        />
        <button
          type="button"
          onClick={handleOpen}
          onMouseDown={(e) => e.stopPropagation()}
          className="absolute bottom-5 right-5 z-10 flex items-center gap-2 px-4 py-2 rounded-lg bg-black/85 backdrop-blur-sm border border-white/10 text-white hover:bg-black transition-colors"
          title="打开图层编辑"
        >
          <SquarePen size={16} />
          <span className="text-[14px] font-medium leading-none">图层编辑</span>
        </button>
      </div>
    );
  }

  return (
    <div
      className="relative flex flex-col items-center justify-center h-full px-16 text-center"
      onDoubleClick={handleOpen}
    >
      {/* 图层结构图标（96px 中性灰，与画布左边栏 left-toolbar.svg 图层编辑选项同款：2×2 圆角方块 + 连接线） */}
      <div className="flex flex-col items-center text-white/40 mb-12">
        <svg width="96" height="96" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
          <rect x="1" y="1" width="10" height="10" rx="2" stroke="currentColor" strokeWidth="2" />
          <rect x="1" y="37" width="10" height="10" rx="2" stroke="currentColor" strokeWidth="2" />
          <rect x="37" y="1" width="10" height="10" rx="2" stroke="currentColor" strokeWidth="2" />
          <rect x="37" y="37" width="10" height="10" rx="2" stroke="currentColor" strokeWidth="2" />
          <path d="M15 6H33" stroke="currentColor" strokeWidth="2" />
          <path d="M15 42H33" stroke="currentColor" strokeWidth="2" />
          <path d="M6 15V33" stroke="currentColor" strokeWidth="2" />
          <path d="M42 15V33" stroke="currentColor" strokeWidth="2" />
        </svg>
      </div>

      {/* 描述文案 */}
      <h3 className="text-[36px] text-white/90 mb-16">在画布中组合、排列与编辑图片图层</h3>

      {/* 主入口按钮（与导演台「打开导演台」同构） */}
      <div className="flex flex-col gap-6 w-full max-w-[440px]">
        <button
          type="button"
          onClick={handleOpen}
          className="w-full h-24 rounded-3xl bg-white/[0.08] border-2 border-white/[0.15] text-[30px] text-white/90 hover:bg-white/[0.14] hover:border-white/[0.30] transition-all"
        >
          打开图层编辑
        </button>
      </div>
    </div>
  );
});

LayerEditContentComponent.displayName = 'LayerEditContent';
export const LayerEditContent = LayerEditContentComponent;
